import { Action, ActionReducerMap, MetaReducer } from '@ngrx/store';
import { environment } from 'src/environments/environment';
import * as fromAirCarrier from './air-carrier/air-carrier.reducer';
import * as fromAircraftMakeModel from './aircraft-make-model/aircraft-make-model.reducer';
import * as fromAircraft from './aircraft/aircraft.reducer';
import * as fromApproachType from './approach-type/approach-type.reducer';
import * as fromAssignmentToDuty from './assignment-to-duty/assignment-to-duty.reducer';
import { AuthStoreState } from './auth/auth-store.model';
import * as fromOktaAuth from './auth/auth-store.reducer';
import * as fromBase from './base/base.reducer';
import * as fromCompany from './company/company.reducer';
import * as fromDepartureType from './departure-type/departure-type.reducer';
import * as fromDrugAlcoholRecord from './drug-alcohol-record/drug-alcohol-record.reducer';
import * as fromDutyLog from './duty-log/duty-log.reducer';
import * as fromEmployee from './employee/employee.reducer';
import * as fromEmploymentRecord from './employment-record/employment-record.reducer';
import * as fromFbo from './fbo/fbo.reducer';
import * as fromFinalDisciplinaryAction from './final-disciplinary-action/final-disciplinary-action.reducer';
import * as fromFlightLogSummary from './flight-log-summary/flight-log-summary.reducer';
import * as fromFlightLog from './flight-log/flight-log.reducer';
import * as fromIdentity from './identity/identity.reducer';
import { clearState } from './meta';
import * as fromNdrRecord from './ndr-record/ndr-record.reducer';
import * as fromPilot from './pilot/pilot.reducer';
import * as fromRecordType from './record-type/record-type.reducer';
import * as fromReportType from './report-type/report-type.reducer';
import * as fromTestType from './test-type/test-type.reducer';
import * as fromTrainingEvent from './training-event/training-event.reducer';
import * as fromTrainingProgram from './training-program/training-program.reducer';
import * as fromTrainingRecord from './training-record/training-record.reducer';
import * as fromTransmissionRunBatchItemSummary from './transmission-run-batch-item-summary/transmission-run-batch-item-summary.reducer';
import * as fromTransmissionRun from './transmission-run/transmission-run.reducer';
import * as fromUserActivityReport from './user-activity-report/user-activity-report.reducer';
/*

To add additional NGRX entity reducers:

1. In the _flight-log-client/src_ directory, execute the following command:

    ng generate entity ./store/EntityName --reducers ./index.ts --spec false --flat false

2. Pluralize the field that was automatically added to AppState (below)

3. Rename the State interface to "{EntityName}Entities" in the {entity}.reducer.ts

4. In the {entity}.reducer.ts file, change the parameter name/typing as demonstrated in this GH
   comment: https://github.com/ngrx/platform/issues/951#issuecomment-425190274. This may not be
   required in the future if the GH issue is resolved.

5. In places where there are relevant actions are performed, dispatch them to the store

*/

export interface AppState {
  // Identity
  identity: fromIdentity.IdentityState;

  // NGRX Entity reducers
  employees: fromEmployee.EmployeeEntities;
  fbos: fromFbo.FboEntities;
  flightLogs: fromFlightLog.FlightLogEntities;
  flightLogSummaries: fromFlightLogSummary.FlightLogSummaryEntities;
  departureTypes: fromDepartureType.DepartureTypes;
  approachTypes: fromApproachType.ApproachTypes;
  companies: fromCompany.CompanyEntities;
  bases: fromBase.BaseEntities;
  aircraft: fromAircraft.AircraftEntities;
  dutyLogs: fromDutyLog.DutyLogEntities;
  auth: AuthStoreState;
  pilots: fromPilot.PilotEntities;
  employmentRecords: fromEmploymentRecord.EmploymentRecordEntities;
  finalDisciplinaryActions: fromFinalDisciplinaryAction.FinalDisciplinaryActionEntities;
  trainingRecords: fromTrainingRecord.TrainingRecordEntities;
  aircraftMakeModels: fromAircraftMakeModel.AircraftMakeModelEntities;
  trainingEvents: fromTrainingEvent.TrainingEventEntities;
  trainingPrograms: fromTrainingProgram.TrainingProgramEntities;
  ndrRecords: fromNdrRecord.NdrRecordEntities;
  assignmentToDuties: fromAssignmentToDuty.AssignmentToDutyEntities;
  drugAlcoholRecords: fromDrugAlcoholRecord.DrugAlcoholRecordEntities;
  airCarriers: fromAirCarrier.AirCarrierEntities;
  recordTypes: fromRecordType.RecordTypeEntities;
  reportTypes: fromReportType.ReportTypeEntities;
  testTypes: fromTestType.TestTypeEntities;
  transmissionRunBatchItemSummaries: fromTransmissionRunBatchItemSummary.TransmissionRunBatchItemSummaryEntities;
  transmissionRuns: fromTransmissionRun.TransmissionRunEntities;
  userActivityReports: fromUserActivityReport.UserActivityReportEntities;
}

export const reducers: ActionReducerMap<AppState> = {
  identity: fromIdentity.reducer,
  employees: fromEmployee.reducer,
  fbos: fromFbo.reducer,
  flightLogs: fromFlightLog.reducer,
  flightLogSummaries: fromFlightLogSummary.reducer,
  departureTypes: fromDepartureType.reducer,
  approachTypes: fromApproachType.reducer,
  companies: fromCompany.reducer,
  bases: fromBase.reducer,
  aircraft: fromAircraft.reducer,
  dutyLogs: fromDutyLog.reducer,
  auth: fromOktaAuth.reducer,
  pilots: fromPilot.reducer,
  employmentRecords: fromEmploymentRecord.reducer,
  finalDisciplinaryActions: fromFinalDisciplinaryAction.reducer,
  trainingRecords: fromTrainingRecord.reducer,
  aircraftMakeModels: fromAircraftMakeModel.reducer,
  trainingEvents: fromTrainingEvent.reducer,
  trainingPrograms: fromTrainingProgram.reducer,
  ndrRecords: fromNdrRecord.reducer,
  assignmentToDuties: fromAssignmentToDuty.reducer,
  drugAlcoholRecords: fromDrugAlcoholRecord.reducer,
  airCarriers: fromAirCarrier.reducer,
  recordTypes: fromRecordType.reducer,
  reportTypes: fromReportType.reducer,
  testTypes: fromTestType.reducer,
  transmissionRunBatchItemSummaries: fromTransmissionRunBatchItemSummary.reducer,
  transmissionRuns: fromTransmissionRun.reducer,
  userActivityReports: fromUserActivityReport.reducer,
};

export const metaReducers: MetaReducer<AppState>[] = !environment.production
  ? [clearState]
  : [clearState];

export type ActionWithPayload = Action & { payload?: any };
